// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * AppBootstrap - V41 boot-rehydratie (V48 gehard)
 * Bij app-mount: als profiel bestaat maar masterKey weg is (na reload/PWA-relaunch)
 * → in DEMO_MODE automatisch opnieuw inloggen met DEMO_PASSWORD.
 * Zo blijft userId+key stabiel over reloads heen.
 *
 * V48-hardening: boot() draait in try/catch/finally zodat de laad-gate ALTIJD oplost.
 * Eerder kon een fout tijdens rehydratie `ready` permanent op false laten staan → blanco
 * scherm. Nu: nette laadstatus, en bij een fout een begrijpelijke melding met herstelactie.
 * De auth-/encryptielogica zelf is ongewijzigd.
 *
 * [TIJDELIJK - voor productie: gebruiker naar /login sturen in plaats van auto-login]
 */
import { useEffect, useState } from 'react'
import { useAuthStore } from '@/store'
import { loadProfile, getMasterKey } from '@/services/profile'
import { DEMO_MODE, DEMO_PASSWORD } from '@/config/demo'

const ACTIVE_USER_KEY = 'wilsapp_active_user'

interface Props { children: React.ReactNode }

export function AppBootstrap({ children }: Props) {
  const { userId, setAuthenticated } = useAuthStore()
  const [ready, setReady] = useState(false)
  const [bootError, setBootError] = useState(false)

  useEffect(() => {
    async function boot() {
      try {
        // Sla userId op voor salt-stabiliteit bij volgende reload
        if (userId) {
          localStorage.setItem(ACTIVE_USER_KEY, userId)
          return
        }

        // Geen userId in store (na reload) - probeer te herstellen
        const storedId = localStorage.getItem(ACTIVE_USER_KEY)
        if (!storedId) return

        // Key al aanwezig (zelfde tab, nog niet verlopen)?
        if (getMasterKey(storedId) !== null) {
          setAuthenticated(storedId)
          return
        }

        // Demo: automatisch opnieuw inloggen
        if (DEMO_MODE) {
          const result = await loadProfile(storedId, DEMO_PASSWORD)
          if (result.succes) {
            setAuthenticated(storedId)
          }
          // Bij falen: ProtectedRoute stuurt door naar /login
        }
      } catch {
        // Onverwachte fout tijdens opstarten - toon nette melding i.p.v. blanco scherm.
        setBootError(true)
      } finally {
        // De laad-gate lost ALTIJD op, ongeacht succes of fout.
        setReady(true)
      }
    }
    boot()
  }, [userId, setAuthenticated])

  // Laadstatus tijdens rehydratie - nooit een leeg scherm.
  if (!ready) {
    return (
      <div className="min-h-screen bg-sand flex flex-col items-center justify-center gap-3">
        <div
          className="h-9 w-9 rounded-full border-[3px] border-sage-200 border-t-sage-600 animate-spin"
          role="status"
          aria-label="Bezig met laden"
        />
        <p className="text-sm text-ink-3">Een ogenblik geduld…</p>
      </div>
    )
  }

  // Begrijpelijke foutmelding met veilige herstelacties (geen technische details).
  if (bootError) {
    return (
      <div className="min-h-screen bg-sand flex flex-col items-center justify-center px-6">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-sand-200 p-6 text-center">
          <h1 className="text-lg font-semibold text-ink mb-2">Het opstarten lukte even niet</h1>
          <p className="text-sm text-ink-3 mb-5 leading-relaxed">
            Er ging iets mis bij het laden van WilsApp. Uw gegevens zijn veilig opgeslagen.
            Probeer het opnieuw.
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="w-full rounded-xl bg-moss text-white py-3 text-sm font-semibold hover:bg-moss-light transition-colors"
            >
              Opnieuw proberen
            </button>
            <button
              type="button"
              onClick={() => { try { localStorage.removeItem(ACTIVE_USER_KEY) } catch { /* genegeerd */ } window.location.reload() }}
              className="w-full rounded-xl border border-sand-300 bg-white text-ink-2 py-3 text-sm font-medium hover:bg-sand-50 transition-colors"
            >
              Naar het inlogscherm
            </button>
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
